/**
 * @author [(Shojib, mahmud.hossain@mlb.com)]
 * @module MilBLinker
 * @description MiLB linker class
 * @name MilB Linker
 */

'use strict';

import BamArgValidator from 'bam-arg-validator';
import BaseLinker from './BaseLinker';

const isArray = Array.isArray;

/**
 * @class MilbLinker
 * @extends Linker
 * @description MiLB Linker that creates the links
 */
class MilbLinker extends BaseLinker {

    /**
     * @method constructor
     * @param options
     */
    constructor(windowObj, options = {}) {

        super(windowObj, options);

        this.HIDDEN_GAMEDAY_TYPES = {
            N: true,
            L: true,
            S: true
        };

        this.milbHostName = 'http://www.milb.com';
        this.milbMobileHostName = 'http://m.milb.com';
    }

    /**
     * @method getStandingsLink
     * @description Return a standings link
     * @return {string} url path
     */
    getStandingsLink(leagueId) {
        const _leagueId = leagueId || this.leagueId;
        return `${this.milbHostName}/standings/index.jsp?lid=${_leagueId}`;
    }

    /**
     * @method getAudioLink
     * @description Return audio link
     * @return {string} url path
     */
    getAudioLink(platform = null) {
        const _teamId = this._teamId;
        if (this._appView !== 'web') {
            return `${this.milbMobileHostName}/gdaudio/${platform || 'iphone'}`;
        }
        return `${this.milbHostName}/milb/multimedia/audio.jsp?sid=t${_teamId}`;
    }

    /**
     * @method getTuneInLinkForContextTeam
     * @description Return tunein link
     * @return {string} url path
     */
    getTuneInLinkForContextTeam(statsapiGame) {
        return _getTuneInLink(statsapiGame, this, 'contextOnly');
    }

    /**
     * @method getTuneInLink
     * @description Return tunein link
     * @return {string} url path
     */
    getTuneInLink(statsapiGame, homeOrAway = 'any') {
        return _getTuneInLink(statsapiGame, this, homeOrAway);
    }

    /**
     * @method getGamedayLink
     * @description Return a gameday link
     * @return {string} url path
     */
    getGamedayLink(statsapiGame, ignoreGameState = false) {
        BamArgValidator.validateStatsapiGame(statsapiGame);
        let { status = {}, gamedayType } = statsapiGame;

        if (!ignoreGameState) {
            if (status.abstractGameCode === 'F' || status.codedGameState === 'S') {
                return null;
            }
        }

        if (this.HIDDEN_GAMEDAY_TYPES[gamedayType]) {
            return null;
        }

        return this.getLink(`/gameday/${statsapiGame.gamePk}`, false);
    }

    /**
     * @method getVideoLink
     * @description Return a video link
     * @return {string} url path
     */
    getVideoLink(contentId) {
        return `${this.milbHostName}/r/video?content_id=${contentId}`;
    }

    /**
     * @method getTvLink
     * @description Return mlb.tv link
     * @return {string} url path
     */
    getTvLink(statsapiGame, liveStream) {
        const gamePk = statsapiGame.gamePk;
        const season = statsapiGame.season;
        let tvURL = '';

        // no milb.tv links before 2019
        if (parseInt(season, 10) > 2018) {
            // check that game is scheduled for streaming unless we're asked only for live streams
            if (statsapiGame.broadcasts && (!liveStream)) {
                let b;
                for (b = 0; b < statsapiGame.broadcasts.length; ++b) {
                    if (statsapiGame.broadcasts[b].id === 1765) {
                        // TODO: ADD DATE TO URL WHEN AWS GRID HAS FUTURE GAMES ON GRID AND DATE ROUTE
                        tvURL = '/live-stream-games';
                    }
                }
            }

            // check media states
            if (statsapiGame.content) {
                let mediaOnOrArchive = false;
                let epg = statsapiGame.content.media && statsapiGame.content.media.epg || [];
                if (!isArray(epg)) {
                    epg = [epg];
                }
                const hasMedia = epg.some(function (epgItem) {
                    if (epgItem.title === 'MiLBTV' && epgItem.items.length) {
                        return epgItem.items.some(function (item) {
                            mediaOnOrArchive = item.mediaState === 'MEDIA_ON' || item.mediaState === 'MEDIA_ARCHIVE';
                            // TODO UPDATE WITH ADDITIONAL MEDIA STATES WHEN THEY ARE ADDED
                            return mediaOnOrArchive;
                        });
                    }
                    return false;
                });
                if (hasMedia) {
                    tvURL = `/live-stream-games/g${gamePk}`;
                }
            }
        }

        if (tvURL.length > 0) {
            return this.getLink(tvURL, false);
        } else {
            return '';
        }

    }

    /**
     * @method getPlayerPageLink
     * @description Return player link
     * @param {object} statsapiPerson Person response object.
     * @param {mixed} [teamId] Optional team id as string or number for building team context urls.
     * @param {string} [platform] Optional platform string (ios, android) for targeting
     *                            device-specific versions of the mobile player cards.
     * @return {string} url path
     */
    getPlayerPageLink(statsapiPerson, teamId = null, platform = null) {
        const _teamId = teamId || this.teamId;

        // @see params above
        BamArgValidator.validateArgs(arguments,
            ['statsapiPerson', '_teamId', 'platform'], ['object', 'string|number', 'string'], [true, false, false]
        );
        BamArgValidator.validateStatsapiPerson(statsapiPerson);

        if (this._appView !== 'web') {
            return `${this.milbMobileHostName}/milb/${platform || 'iphone'}/player/${statsapiPerson.id}`;
        }

        if (_teamId) {
            return `/r/player/${_teamId}/${statsapiPerson.id}`;
        } else {
            return `/r/player/${statsapiPerson.id}`;
        }
    }

    /**
     * If a game is in pregame, return a gameday preview link, otherwise null
     * If a plain gameday link is available, don't return a preview link
     * @param statsapiGame
     * @return {string}
     */
    getPreviewLink(statsapiGame) {

        BamArgValidator.validateStatsapiGame(statsapiGame);

        if (statsapiGame.status) {

            let code = statsapiGame.status.abstractGameCode;

            if (code && code === 'P') {

                let gameDayLink = this.getGamedayLink(statsapiGame);

                if (!gameDayLink) {
                    return this.getLink(`/gameday/${statsapiGame.gamePk}/preview`, false);
                }

            }

        }

        return null;
    }

    /**
     * @method getBoxscoreLink
     * @description Return a gameday link
     * @return {string} url path
     */
    getBoxscoreLink(statsapiGame, ignoreGameState = false) {
        BamArgValidator.validateStatsapiGame(statsapiGame);

        if (statsapiGame.status) {
            let code = statsapiGame.status.abstractGameCode;

            if (code && code === 'F') {
                let statusCode = statsapiGame.status.statusCode;
                let firstLetter = statusCode && statusCode.charAt(0) || '';

                if (firstLetter && firstLetter !== 'D' && firstLetter !== 'C') {
                    return this.getLink(`/gameday/${statsapiGame.gamePk}/final/box`, false);
                }
            }
        }

        return null;
    }

    /**
     * @method getTeamHomepageLink
     * @description Return team homepage url
     * @return {string} url path
     */
    getTeamHomepageLink(statsapiTeam) {
        BamArgValidator.validateStatsapiTeam(statsapiTeam);
        const teamId = statsapiTeam.id || this.teamId;
        return `${this.milbHostName}/index.jsp?sid=t${teamId}`;
    }

    /**
     * @method getWrapLink
     * @description Return wrap link
     * @return {string} url path
     */
    getWrapLink(statsapiGame, sId) {
        BamArgValidator.validateStatsapiGame(statsapiGame);
        let { content = {} } = statsapiGame;
        let gid = _getGID(statsapiGame, this);

        //TODO: Find out if this should use content.editorial.milb_recap instead of hasWrapArticle
        if (content.summary) {
            let { hasWrapArticle } = content.summary;

            if (hasWrapArticle) {
                const siteId = sId || this.siteId;
                return _getStatsLink(this.milbHostName, gid, siteId);
            }
        }

        return null;
    }

    /**
     * @method getStatsLink
     * @description Return stats jsp link
     * @return {string} url path
     */
    getStatsLink(statsapiGame, sId) {
        let gid = _getGID(statsapiGame, this);
        const siteId = sId || this.siteId;
        return _getStatsLink(this.milbHostName, gid, 'milb');
    }



    /**
     * Returns a player-page link based on player ID and team ID.
     * @param {object} leader The player's leader object.
     * @return {string} The player-page link for example: http://www.milb.com/player/index.jsp?sid=t234&player_id=642715
     */
    getPlayerPageLinkById(leader) {
        const complexLeague = [121, 124, 130]; // DIGIT-1409 different player link on Complex leagues.
        const leagueId = leader.league.id;
        let playerPageLink = '';
        if (leader) {
            const sid = leagueId && complexLeague.indexOf(leagueId) !== -1 ? 'milb' : `t${leader.team.id}`;
            playerPageLink = `${this.milbHostName}/player/index.jsp?sid=${sid}&player_id=${leader.person.id}`;
        }
        return playerPageLink;
    }

    /**
     * Generates a tickets link based on game; whether user is desktop or mobile; what the current team is (we don't
     * yet handle league sites here, just club sites); and what types of links are available from statsapi (e.g., if
     * there's no mobile-specific link, we return the desktop link, etc.).
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string}
     */
    getTicketsLink(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'any');
    }

    /**
     * Returns a ticket link IF AND ONLY IF there is one available which will take the user to a site where they can
     * purchase tickets from the context team (the team whose site they're visiting).  This method is meant to be used
     * in conjunction with getTicketsLinkNotForContextTeam(), which returns the "opposite" of what this method returns:
     * only non-context-team ticket links.
     *
     * This method (and its cousin getTicketsLinkNotForContextTeam()) are meant to be used instead of getTicketsLink()
     * when the consumer must know whether they're dealing with a context-team link or an opponent-team link (since in
     * some cases, we alert the user to the fact that they'll be purchasing tickets from the opponent if they proceed.
     * Expected use" consumer calls this method first; if it returns null, they call getTicketsLinkNotForContextTeam()
     * and offer the return value (if not null ofc) to the user, but telling them this is the opponent's link.
     * @param statsapiGame
     * @return {string} A url, or null if a ticket link meeting the criteria described above is not available
     */
    getTicketsLinkForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'contextOnly');
    }
    
    /**
     * @see #getNGETicketsLinkForContextTeam - where that method returns links to buy tickets from the context team, this
     *   method returns links to buy tickets from the opponent team.
     * @param statsapiGame
     */
    getNGETicketsLinkForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'opponentOnly', true);
    }

    /**
     * @see #getTicketsLinkForContextTeam - where that method returns links to buy tickets from the context team, this
     *   method returns links to buy tickets from the opponent team.
     * @param statsapiGame
     */
    getTicketsLinkNotForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'opponentOnly');
    }

    /**
     * @method getTeamStatsLink
     * @description Return team stats url
     * @return {string} url path
     */
    getTeamStatsLink(teamId) {
        return teamId ? `${this.milbHostName}/milb/stats/stats.jsp?t=t_ibp&cid=${teamId}&stn=true&sid=t${teamId}` : null;
    }

     /**
     * @method getTeamHomepageLinkFromTeamId
     * @description Return team homepage url
     * @return {string} url path
     */
    getTeamHomepageLinkFromTeamId(teamId) {
        const slug = (this._teamSlugs && this._teamSlugs[teamId]);
        return (slug) ? this.getLink(`/${slug}`, false) : null;
    }

    /**
     * @method getScheduleLink
     * @description Return schedule url maintaining team/league context if applicable
     * @return {string} url path
     */
    getScheduleLink() {
        return this.getLink('/schedule', true);
    }
}


/**
 * @method _getStatsLink
 * @private
 * @description Return a stats link
 * @return {string} url path
 */

function _getStatsLink(host, gid, siteId) {
    return `${host}/milb/stats/stats.jsp?gid=${gid}&t=g_rcp&sid=${siteId}`;
}
/**
 * Return the specified ticket link if available, otherwise null
 * @param statsapiGame A SD API game node
 * @param forceDesktop If true, only desktop links will be returned; if false, mobile ones will be returned as well
 * @param linker the MlbLinker instance calling this function
 * @param sellerType Which type of seller's link is okay to return?  Three possible values:
 *          - 'contextOnly': only return link to seller associated with the context team
 *          - 'opponentOnly': only return link to seller asociated with the opponent
 *          - 'any' expected to be called only when there isn't a context (i.e., the league site), returns the home
 *            team's seller's link if it exists; otherwise returns the away team's seller's link
 *
 * @return {string} A URL or null
 * @private
 */
function _getTicketsLink(statsapiGame, forceDesktop, linker, sellerType, nonGameEvent) {
    if (!nonGameEvent) {
        BamArgValidator.validateStatsapiGame(statsapiGame);
        BamArgValidator.validateArgs(arguments, ['statsapiGame', 'forceDesktop', 'linker', 'sellerType'],
            ['object', 'boolean', 'object', 'string'], [true, true, true, true]);
    } else {
        BamArgValidator.validateStatsapiNonGameEvent(statsapiGame);
    }
    BamArgValidator.validateAllowed(sellerType, ['any', 'contextOnly', 'opponentOnly']);

    if (sellerType !== 'any' && !linker._teamId || (statsapiGame.status && statsapiGame.status.abstractGameCode === 'F')) {
        return null;
    }

    if (statsapiGame.tickets) {
        const awayId = !nonGameEvent 
            ? statsapiGame.teams.away.team.id
            : (statsapiGame.teams[0] && statsapiGame.teams[0].id);
        let url = null;
        let homeOrAway = +linker._teamId === awayId ? 'away' : 'home';

        if (sellerType === 'any') {
            url = _getTicketsLinkOfType(statsapiGame.tickets, 'home', linker)
                || _getTicketsLinkOfType(statsapiGame.tickets, 'away', linker);
        } else {
            homeOrAway = (sellerType === 'contextOnly')
                ? (awayId === +linker._teamId ? 'away' : 'home')
                : (awayId !== +linker._teamId ? 'away' : 'home');

            if (nonGameEvent) {
                homeOrAway = 'home';
            }

            url = _getTicketsLinkOfType(statsapiGame.tickets, homeOrAway, linker);
        }

        if (url !== null) {
            const homeTeam = statsapiGame.teams.home && statsapiGame.teams.home.team;
            // handle games where home team is not playing in their home venue
            if (homeTeam && +linker._teamId === homeTeam.id && statsapiGame.venue.id !== homeTeam.venue.id) {
                homeOrAway = 'away';
            }
            // prepend redirect url to interstitial
            url = `/tickets/interstitial/${homeOrAway}?jumpTo=${encodeURIComponent(url)}`;

            return url;
        }
    }

    // If deviceType is mobile and there is no mobile link, try to return the desktop link instead
    if (!forceDesktop && linker._deviceType !== 'desktop') {
        return _getTicketsLink(statsapiGame, true, linker, sellerType);
    }

    return null;
}

/**
 * Returns a ticket link based on the specified type (home or away) and other criteria
 * @param ticketsArr Array of
 * @param homeOrAway
 * @param linker an MlbLinker instance
 * @private
 */
function _getTicketsLinkOfType(ticketsArr, homeOrAway, linker) {
    const ticketType = (linker._deviceType === 'mobile' ? 'mobile' : 'wired');
    const ln = ticketsArr.length;
    let ticketEl;
    let i;

    for (i = 0; i < ln; i++) {
        ticketEl = ticketsArr[i];
        if (ticketEl.ticketType && ticketEl.ticketType === ticketType) {
            if (ticketEl.ticketLinks && ticketEl.ticketLinks[homeOrAway]) {
                return ticketEl.ticketLinks[homeOrAway];
            }
        }
    }

    return null;
}


/**
 * Return the specified TuneIn audio link if available, otherwise null
 * @param statsapiGame A SD API game node
 * @param linker the MlbLinker instance calling this function
 * @param homeAwayContext Which team's audio is okay to return?  Three possible values:
 *          - 'contextOnly': only return audio associated with the context team
 *          - 'home': only return audio asociated with the home team
 *          - 'away': only return audio asociated with the away team
 *          - 'any' expected to be called only when there isn't a context (i.e., the league site), returns the home
 *            team's audio link if it exists; otherwise returns the away team's audio link
 *
 * @return {string} A URL or null
 * @private
 */
function _getTuneInLink(statsapiGame, linker, homeAwayContext) {
    BamArgValidator.validateStatsapiGame(statsapiGame);
    BamArgValidator.validateArgs(arguments, ['statsapiGame', 'linker', 'homeAwayContext'],
        ['object', 'object', 'string'], [true, true, true, true]);
    BamArgValidator.validateAllowed(homeAwayContext, ['contextOnly', 'home', 'away', 'any']);

    let url = null;
    if (statsapiGame.broadcasts) {
        let showHome = homeAwayContext === 'home';
        let showAway = homeAwayContext === 'away';

        if (homeAwayContext === 'contextOnly') {
            const awayId = statsapiGame.teams.away.team.id;
            showHome = linker._teamId !== awayId;
            showAway = linker._teamId === awayId;
        }
        if (homeAwayContext === 'any') {
            showHome = true;
            showAway = true;
        }

        let b;
        for (b = 0; b < statsapiGame.broadcasts.length; ++b) {
            if (statsapiGame.broadcasts[b].id === 4955) {
                // RETURNS LINK TO PAGEBUILDER AWS UNTIL API RETURNS SPECIFIC LINKS
                // BDFED-2392
                if (showHome && statsapiGame.broadcasts[b].homeAway === 'home') {
                    return linker.getLink('live-stream-games/audio', false);
                }
                if (showAway && statsapiGame.broadcasts[b].homeAway === 'away') {
                    return linker.getLink('live-stream-games/audio', false);
                }
            }
        }
    }
    return url;
}



function _getGID(statsapiGame, linker) {

    const awayTeamCode = statsapiGame.teams.away.team.teamCode;
    const homeTeamCode = statsapiGame.teams.home.team.teamCode;
    const awaySportCode = linker.sportCodeMap[statsapiGame.teams.away.team.sport.id];
    const homeSportCode = linker.sportCodeMap[statsapiGame.teams.home.team.sport.id];
    const gameNumber = statsapiGame.gameNumber;
    let gameDate = statsapiGame.gameDate.substr(0, 10);
    gameDate = gameDate.replace(/-/g, '_');

    return `${gameDate}_${awayTeamCode}${awaySportCode}_${homeTeamCode}${homeSportCode}_${gameNumber}`;

}

export default MilbLinker;

